import React, { useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "./VideoPlayer.css";
import BackwardIcon from "assets/icons/video_backward.svg";
import ForwardIcon from "assets/icons/video_forward.svg";
import ScreenshotIcon from "assets/icons/video_screenshot.svg";
import DownloadIcon from "assets/icons/video_download.svg";

import { FaShareFromSquare } from "react-icons/fa6";
import styled from "styled-components";
import FileSaver from "file-saver";
import {
  DownloadUrlResponse,
  VideoShareViewResult,
} from "api/interfaces/videoshareInterface.interface";
import Snapshot from "pages/videoshare/view/Snapshot";
import { getVideoDownloadUrl } from "api/videoshareAPI";
import * as mediaQuery from "components/MediaQuery";
import moment from "moment";
import { convertVideoStartMs, generateEpochToFileName } from "utils/timeUtil";
import { openNewWindow } from "utils/functions";
import { useMediaQuery } from "react-responsive";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { SUSPENSION_STATUS } from "redux/stomp/stompSubscriptionSlice";

type VideoPlayerProps = {
  videoUrl: string;
  videoResult?: VideoShareViewResult;
  options: any;
  onReady: (player: any) => void;
  canDownload?: boolean;
  videoId?: string;
  isAuth?: boolean;
  title?: string;
  timezone: string;
  onClickShare?: () => void;
  isDisabledAdd?: boolean;
  guestStartMs?: string;
  uploadStatus?: string;
  videoTimezone: string;
};

const videoJsOptions = {
  autoplay: false,
  controls: true,
  responsive: true,
  fill: true,
  //fluid: true,
  crossorigin: "anonymous",
  breakpoints: {
    medium: 500,
  },
  controlBar: {
    pictureInPictureToggle: false,
    captureButton: false,
    subCount: false,
  },
};

const clickEvent = (function () {
  if ("ontouchend" in document.documentElement === true) {
    return "touchend";
  } else {
    return "click";
  }
})();
export const getCaptionUrl = (url: string) => {
  return url.replace(/(.mp4|.m3u8)$/, ".vtt");
};
export const VideoPlayer = ({
  videoUrl,
  videoResult,
  options,
  onReady,
  canDownload,
  videoId,
  isAuth,
  title,
  timezone,
  onClickShare,
  isDisabledAdd,
  guestStartMs,
  uploadStatus,
  videoTimezone,
}: VideoPlayerProps) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const canvasRef = useRef<any>(null);

  const [isSnapshot, setIsSnapshot] = useState<boolean>(false);
  const [capturedBlob, setCapturedBlob] = useState<Blob>();
  const [capturedVtt, setCapturedVtt] = useState<string>();
  // const [isOpenPlaylist, setIsOpenPlaylist] = useState<boolean>(false);
  const [nowUrl, setNowUrl] = useState<string>();
  const isDesktop = useMediaQuery({ minWidth: mediaQuery.desktop });
  const { isSuspended }: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  useEffect(() => {
    if (playerRef.current) {
      const player: videojs.Player = playerRef.current;
      const findShareButton = player.controlBar.getChildById("shareButtonComp");
      if (findShareButton) {
        if (!isDisabledAdd) {
          findShareButton.hide();
        } else {
          findShareButton.show();
        }
      }
    }
  }, [isDisabledAdd]);

  const onModelSnapshot = useCallback(() => {
    setIsSnapshot(!isSnapshot);
  }, [isSnapshot]);

  const capture = useCallback(() => {
    if (capturedBlob !== undefined) {
      const fileDate =
        capturedVtt !== undefined
          ? capturedVtt.replaceAll(`/`, ``).replace(/ /g, "_")
          : moment().format("MMDDyyyy_HHmmss");

      FileSaver.saveAs(
        capturedBlob as Blob,
        (title ? title : "guest") + ")" + fileDate + ".png"
      );
    }
  }, [capturedBlob, capturedVtt, title]);

  const onCapture = useCallback(async () => {
    const player: videojs.Player = playerRef.current;

    const canvas: any = document.createElement("CANVAS");

    canvas.width = player.videoWidth();
    canvas.height = player.videoHeight();

    // recorderName 이 있을 경우는 자막이 없다면 undefined 처리하고 그외는 캡쳐시간이 없을 경우 대비: 비디오 시작시간+현재재생시간
    let currentTime = videoResult?.recorderName
      ? undefined
      : convertVideoStartMs(
          String(
            player.currentTime() * 1000 +
              (videoResult?.startTimeMs ? videoResult?.startTimeMs : 0)
          ),
          videoTimezone
        );

    const textTracks = player.textTracks();
    for (var i = 0; i < textTracks.length; i++) {
      var track = textTracks[i];
      if (track.mode === "showing") {
        const cues = track.activeCues;
        if (cues !== null && cues.length > 0) {
          currentTime = (cues[0] as VTTCue).text;
        }
        break;
      }
    }
    setCapturedVtt(currentTime);
    canvas
      .getContext("2d")
      .drawImage(player.children_[0], 0, 0, canvas.width, canvas.height);

    var dataUri = canvas.toDataURL("image/png");
    var data = dataUri.split(",")[1];
    var mimeType = dataUri.split(";")[0].slice(5);

    var bytes = window.atob(data);
    var buf = new ArrayBuffer(bytes.length);
    var arr = new Uint8Array(buf);

    for (let i = 0; i < bytes.length; i++) {
      arr[i] = bytes.charCodeAt(i);
    }

    var blob = new Blob([arr], { type: mimeType });
    setCapturedBlob(blob);
    setIsSnapshot(true);
    //FileSaver.saveAs(blob as Blob, "capture.png");
  }, [videoTimezone, videoResult]);

  // const download = () => {
  //   const title = state !== undefined ? state.title : new Date().toISOString();
  //   fetch(options.sources[0].src+"&response-content-disposition=attachment", { method: "GET" })
  //     .then((response) => response.blob())
  //     .then((blob) => FileSaver.saveAs(blob, title + ".mp4"))
  //     .catch((err) => {
  //       console.error("err: ", err);
  //     });
  // };

  const download = useCallback(() => {
    if (videoId === undefined) {
      return;
    }
    const player: videojs.Player = playerRef.current;
    const playUrl = player.getMedia().src as videojs.Tech.SourceObject[];

    if (playUrl !== undefined && playUrl.length > 0) {
      const downTime = generateEpochToFileName(
        guestStartMs !== undefined
          ? guestStartMs
          : String(videoResult?.startTimeMs),
        timezone
      );
      //BUG [RND-411] 자막다운로드 추가
      getVideoDownloadUrl(videoId, 0, title + "_" + downTime)
        .then((res: DownloadUrlResponse) => {
          if (res.error === 0 && res.result !== undefined) {
            if (res.result[0] !== undefined) {
              //window.open(res.result[0]);
              openNewWindow(res.result[0]);
            }

            if (res.result[1] !== undefined) {
              window.open(res.result[1]);
            }

            // if(res.result[1] !== undefined){
            //   const link2 = document.createElement("a");
            //   link2.href = res.result[1] ;
            //   link2.target = "_self";
            //   link2.setAttribute('download', `22222222222222222`);
            //   link2.click();
            //    link2.remove();
            // }
            // axios({
            //   url: res.result,
            //   method: "GET",
            //   responseType: "blob",
            // }).then((response) => {
            //   const url = window.URL.createObjectURL(new Blob([response.data]));
            //   const link = document.createElement("a");
            //   link.href = url;
            //   link.setAttribute("download", `${title}_${moment().format("MMDDyyyy_HHmmss")}.mp4`);
            //   document.body.appendChild(link);
            //   link.click();
            // });
          }
        })
        .catch();
    }
  }, [nowUrl, videoId]);

  const backward = () => {
    const player: videojs.Player = playerRef.current;

    const now = player.currentTime();
    player.currentTime(Math.max(0, now - 10));
  };

  const forward = () => {
    const player: videojs.Player = playerRef.current;
    let duration = player.duration();

    const now = player.currentTime();

    if (player.liveTracker && player.liveTracker.isLive()) {
      duration = player.liveTracker.seekableEnd();
    }
    player.currentTime(Math.min(now + 10, duration));
  };

  useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
      const videoElement = document.createElement("video-js");

      videoElement.className = "videojs-big-play-centered";

      if (videoRef.current) {
        videoRef.current.appendChild(videoElement);
      }

      const player: videojs.Player = (playerRef.current = videojs(
        videoElement,
        videoJsOptions,
        () => {
          videojs.log("player is ready");
          onReady && onReady(player);
        }
      ));

      player.crossOrigin("anonymous");
      var playButton: any = player.controlBar.getChild("playToggle");
      var indexPlay = player.controlBar.children().indexOf(playButton) + 1;
      var fullscreen: any = player.controlBar.getChild("FullscreenToggle");
      var index = player.controlBar.children().indexOf(fullscreen);
      if (isSuspended !== SUSPENSION_STATUS.ENABLED) {
        return;
      }

      if (isDesktop) {
        //forward
        var forwardButton = player.controlBar.addChild("Button", {}, indexPlay);
        var forwardButtonDom: Element = forwardButton.el();
        const forwardImage = new Image();
        forwardImage.className = "custom-video-button";
        forwardImage.src = ForwardIcon;
        forwardButtonDom.appendChild(forwardImage);
        forwardButtonDom.addEventListener(clickEvent, () => forward());

        //backward
        var backButton = player.controlBar.addChild("Button", {}, indexPlay);
        var backButtonDom: Element = backButton.el();
        const backImage = new Image();
        backImage.className = "custom-video-button";
        backImage.src = BackwardIcon;
        backButtonDom.appendChild(backImage);
        //backButtonDom.onclick = backward;
        backButtonDom.addEventListener(clickEvent, () => backward());

        //download
        if (canDownload !== undefined && canDownload === false) {
          // not support download
        } else if (uploadStatus === "success") {
          var downloadButton = player.controlBar.addChild(
            "Button",
            { id: "" },
            index
          );
          var downloadButtonDom: Element = downloadButton.el();
          const downloadImage = new Image();
          downloadImage.className = "custom-video-button";
          downloadImage.src = DownloadIcon;
          downloadButtonDom.appendChild(downloadImage);

          //downloadButtonDom.onclick = download;
          downloadButtonDom.addEventListener(clickEvent, () => download());
        }

        //capture
        // if (isAuth !== undefined && isAuth === false) {
        //   // not support capture
        // } else {
        var captureButton = player.controlBar.addChild("Button", {}, index);
        var captureButtonDom: Element = captureButton.el();
        const captureImage = new Image();
        captureImage.className = "custom-video-button";
        captureImage.src = ScreenshotIcon;
        captureButtonDom.appendChild(captureImage);
        //captureButtonDom.onclick = onCapture;
        captureButtonDom.addEventListener(clickEvent, () => {
          if (videoId === undefined) {
            return;
          }
          const player: videojs.Player = playerRef.current;
          const playUrl = player.getMedia().src as videojs.Tech.SourceObject[];
          if (playUrl !== undefined && playUrl.length > 0) {
            onCapture();
          }
        });

        //Spacer
        var betweenSpacer = player.controlBar.addChild("Spacer", {}, index);
        betweenSpacer.addClass("vjs-spacecenter");
      } else {
        if (canDownload !== undefined && canDownload === false) {
          // not support download
        } else {
          const downloadButton = player.controlBar.addChild(
            "Button",
            { id: "video_player_down" },
            index
          );
          const downloadButtonDom: Element = downloadButton.el();
          const downloadImage = new Image();
          downloadImage.className = "custom-video-button";
          downloadImage.src = DownloadIcon;
          downloadButtonDom.appendChild(downloadImage);

          //downloadButtonDom.onclick = download;
          downloadButtonDom.addEventListener(clickEvent, async () => {
            if (videoId === undefined) {
              return;
            }

            const player: videojs.Player = playerRef.current;
            const playUrl = player.getMedia()
              .src as videojs.Tech.SourceObject[];

            if (playUrl !== undefined && playUrl.length > 0) {
              const downTime = generateEpochToFileName(
                guestStartMs !== undefined
                  ? guestStartMs
                  : String(videoResult?.startTimeMs),
                timezone
              );
              //BUG [RND-411] 자막다운로드 추가
              getVideoDownloadUrl(videoId, 0, title + "_" + downTime)
                .then((res: DownloadUrlResponse) => {
                  if (res.error === 0 && res.result !== undefined) {
                    // eslint-disable-next-line no-restricted-globals
                    if (confirm("Would you like to download?")) {
                      let fileUrl = res.result[0];
                      let link = document.createElement("a");
                      link.href = fileUrl;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }
                  }
                })
                .catch();
            }

            //download();
          });
        }

        const captureButton = player.controlBar.addChild("Button", {}, index);
        const captureButtonDom: Element = captureButton.el();
        const captureImage = new Image();
        captureImage.className = "custom-video-button";
        captureImage.src = ScreenshotIcon;
        captureButtonDom.appendChild(captureImage);
        //captureButtonDom.onclick = onCapture;
        captureButtonDom.addEventListener(clickEvent, () => {
          if (videoId === undefined) {
            return;
          }
          const player: videojs.Player = playerRef.current;
          const playUrl = player.getMedia().src as videojs.Tech.SourceObject[];
          if (playUrl !== undefined && playUrl.length > 0) {
            onCapture();
          }
        });
        // }
      }

      //Share
      if (
        isAuth !== undefined &&
        isAuth === true &&
        isSuspended !== SUSPENSION_STATUS.ENABLED
      ) {
        const shareButton = player.controlBar.addChild(
          "Button",
          { id: "shareButtonComp" },
          index
        );
        var shareButtonDom: Element = shareButton.el();
        const root = createRoot(shareButtonDom);

        const shareIcon = <FaShareFromSquare size={16} />;
        root.render(shareIcon);
        //shareButtonDom.onclick = onClickShare;
        shareButtonDom.addEventListener(clickEvent, () => {
          if (onClickShare) {
            onClickShare();
          }
        });

        if (!isDisabledAdd) {
          shareButton.hide();
        }
      }

      // You could update an existing player in the `else` block here
      // on prop change, for example:
    } else {
      // const player: videojs.Player = playerRef.current;
      // //TODO player List 관리 필요. video url is multi!!!
      // const firstUrl: string =
      //   videoUrl !== undefined && videoUrl.length > 0 ? videoUrl[0].url : "";
      // setNowUrl(firstUrl);
      // // player.autoplay(options.autoplay);
      // player.src({
      //   src: firstUrl,
      //   type: "video/mp4",
      // });
      // const trackEl = player.addRemoteTextTrack(
      //   {
      //     kind: "captions",
      //     label: "timestamp",
      //     language: "en",
      //     src: getCaptionUrl(firstUrl),
      //   },
      //   false
      // );
      // trackEl.addEventListener("load", function () {
      //   var tracks = player.textTracks();
      //   for (var i = 0; i < tracks.length; i++) {
      //     var track = tracks[i];
      //     if (track.label === "timestamp") {
      //       track.mode = "showing";
      //     }
      //   }
      // });
      // player.on('ended', onPlayEnded);
    }
  }, [onReady, options]);
  // console.log(videoUrl);

  useEffect(() => {
    if (videoUrl !== undefined) {
      const player: videojs.Player = playerRef.current;

      //TODO player List 관리 필요. video url is multi!!!
      setNowUrl(videoUrl);
      // if (videoUrl.length > 1) {
      //   setIsSegment(true);
      //   setIsOpenPlaylist(true);
      // } else {
      //   setIsSegment(false);
      // }
      // player.autoplay(options.autoplay);

      const videoType = videoUrl.endsWith("m3u8")
        ? "application/x-mpegURL"
        : "video/mp4";

      player.src({
        src: encodeURI(videoUrl).replace("#", "%23"),
        type: videoType,
      });
      player.autoplay();
      if (isDesktop && videoResult?.recorderName) {
        const trackEl = player.addRemoteTextTrack(
          {
            kind: "captions",
            label: "timestamp",
            language: "en",
            src: encodeURI(getCaptionUrl(videoUrl)).replace("#", "%23"),
          },
          false
        );
        trackEl.addEventListener("load", function () {
          var tracks = player.textTracks();

          for (var i = 0; i < tracks.length; i++) {
            var track = tracks[i];
            if (track.label === "timestamp") {
              track.mode = "showing";
            }
          }
        });
      } else {
        const tracks = player.textTracks();
        for (let i = 0; i < tracks.length; i++) {
          const track = tracks[i];
          if (track.label === "timestamp") {
            track.mode = "disabled";
            break;
          }
        }
      }
    }
  }, [videoUrl, isDesktop]);

  // const onChangeOpenPlaylist = () => {
  //   setIsOpenPlaylist(!isOpenPlaylist);
  // };

  // Dispose the Video.js player when the functional component unmounts
  React.useEffect(() => {
    const player: videojs.Player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  // const onClickThumbnail = useCallback(
  //   (url: string) => {
  //     const player: videojs.Player = playerRef.current;

  //     if (player && videoUrl !== "") {
  //       setNowUrl(videoUrl);

  //       const videoType = videoUrl.endsWith("m3u8")
  //         ? "application/x-mpegURL"
  //         : "video/mp4";

  //       player.src({
  //         src: encodeURI(videoUrl).replace("#", "%23"),
  //         type: videoType,
  //       });

  //       const trackEl = player.addRemoteTextTrack(
  //         {
  //           kind: "captions",
  //           label: "timestamp",
  //           language: "en",
  //           src: encodeURI(getCaptionUrl(videoUrl)).replace("#", "%23"),
  //         },
  //         false
  //       );
  //       trackEl.addEventListener("load", function () {
  //         var tracks = player.textTracks();

  //         for (var i = 0; i < tracks.length; i++) {
  //           var track = tracks[i];
  //           if (track.label === "timestamp") {
  //             track.mode = "showing";
  //           }
  //         }
  //       });

  //       player.play();
  //     }
  //   },
  //   [videoUrl]
  // );

  // const onPlayEnded = useCallback(() => {
  //   const findIndex: number = videoUrl.findIndex(
  //     (video) => video.url === nowUrl?.url
  //   );

  //   if (findIndex !== -1 && findIndex !== videoUrl.length) {
  //     onClickThumbnail(videoUrl[findIndex + 1].url);
  //   }
  // }, [videoUrl, nowUrl]);

  return (
    <>
      <Wrapper data-vjs-player id="videop">
        <VideoWrapper
          ref={videoRef}
          onContextMenu={(e) => e.preventDefault()}
        />
        <div id="captureDiv" style={{ overflow: "hidden", height: 0 }}>
          <canvas id="captureCanvas" ref={canvasRef} />
        </div>
        {/* {!isOpenPlaylist && videoUrl !== undefined && videoUrl.length > 1 && (
          <button
            className="playlist-button"
            onClick={() => setIsOpenPlaylist(true)}
          >
            <img src={LIST_ICON} alt="chevron left button" />
          </button>
        )}

        {isOpenPlaylist && (
          <Playlist
            playList={videoUrl}
            onClickHideList={() => setIsOpenPlaylist(false)}
            onClickThumbnail={onClickThumbnail}
            nowUrl={nowUrl?.url}
            timezone={timezone}
          />
        )} */}
      </Wrapper>
      {isSnapshot && (
        <Snapshot
          isAuth={isAuth}
          videoId={videoId}
          capture={capture}
          capturedBlob={capturedBlob}
          capturedVtt={capturedVtt}
          onModelSnapshot={onModelSnapshot}
        />
      )}
    </>
  );
};

export const MemoVideoPlayer = VideoPlayer;
const Wrapper = styled.div`
  width: 100%;
  position: relative;
  height: 100%;

  .vjs-control-bar {
    z-index: 9;
    width: 100%;
  }

  .playlist-button {
    z-index: 10;
    position: absolute;
    top: 10px;
    right: 10px;
  }

  .custom-video-button {
    height: 70%;
  }
`;

const VideoWrapper = styled.div`
  width: 100%;
  height: 100%;
  ${mediaQuery.isTablet} {
    height: auto;
    aspect-ratio: 16/9;
  }
`;
export default VideoPlayer;
